import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CustomText, StatusType, TaskType } from "App.types";
import { withHistory } from "slate-history";
import { createEditor, Editor, Transforms } from "slate";
import { Button } from "antd";
import API from "Api";
import { withCustomLogic } from "App.helpers";
import { CloseOutlined, SoundFilled } from "@ant-design/icons";
import SentenceMaskedLeaf from "./SentenceLeaf";
import styles from "./SentenceTask.module.scss";
import { usePressEnter } from "./Helpers/usePressEnter";
import { SentenceTaskProps } from "./SentenceTask.type";
import cx from "classnames";
import { trim } from "lodash";
import TaskPanel from "Components/TaskPanel";
import { NotifyContext } from "App";

const initialValue = [{ children: [{ text: "" }] }];

const SentenceTask: FC<SentenceTaskProps> = ({
  sentence,
  sentence: { id, text, translate, storyId, button },
  activeType,
  lesson,
  onTaskComplete,
  onNext,
  children,
  audio,
  setDictOpened,
  alignCenter = false,
  setTry,
  showGrammar,
  noText,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [activeLeaf, setActiveLeaf] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const editor = useMemo(() => withReact(withHistory(withCustomLogic(createEditor()))), []);
  const notifyApi = useContext(NotifyContext);

  const setInitialState = useCallback(
    (withFocus = true) => {
      editor.children.forEach(() => {
        Transforms.delete(editor, { at: [0] });
      });

      editor.children = [];

      Transforms.insertNodes(editor, [{ children: [{ text: "" }] }], { select: withFocus });
      if (withFocus)
        try {
          ReactEditor.focus(editor);
        } catch (e) {}
    },
    [editor],
  );

  // initial
  useEffect(() => {
    setActiveLeaf(null);
    setStatus(StatusType.Editing);
    setInitialState();
  }, [sentence.id, editor, setInitialState]);

  const renderLeaf = useCallback(
    (props: any) => (
      <SentenceMaskedLeaf
        underlined={props.leaf.audioIdx === (activeLeaf ?? -1)}
        showErrors={!["editing", "completed", "loading", "isRecording", ""].includes(status)}
        {...props}
      />
    ),
    [status, activeLeaf],
  );

  const onCheck = useCallback(
    async (textAnswer: string = "") => {
      const userAnswerText = trim(textAnswer || editor.children[0]?.children?.map((el: CustomText) => el.text).join("")) || "";

      if (!userAnswerText) return notifyApi?.info({ message: "выполни задание!" });

      // if (isEqualText(userAnswerText, translate) || alternatives.some((alt) => isEqualText(userAnswerText, alt.translate))) {
      //   return onComplete();
      // }
      setIsLoading(true);
      setTry();

      const { comments, wrong } = await API.sentence
        .check(sentence.id, { answer: userAnswerText, lessonId: lesson.id })
        .finally(() => setIsLoading(false));
      notifyApi.destroy();
      if (wrong) {
        notifyApi.error({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
        API.event.save({ sentence, text: userAnswerText, type: "mistake", task: activeType, lesson: { id: lesson.id } });
      } else {
        comments && notifyApi.success({ message: <div dangerouslySetInnerHTML={{ __html: comments }} />, duration: 10 });
        setStatus(StatusType.Completed);
        return onTaskComplete(userAnswerText);
      }
    },
    [editor.children, notifyApi, setTry, sentence, lesson.id, activeType, onTaskComplete],
  );

  const onHintAnswer = () => {
    const text = editor.children[0]?.children?.map((el: CustomText) => el.text).join("");
    API.event.save({ text, type: "hint", task: activeType, lesson: { id: lesson.id }, sentence });

    setTry(true);
    if (editor.children.length) {
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });
    }
    Transforms.removeNodes(editor);
    Transforms.insertNodes(editor, [{ children: [{ text: translate }] }]);
  };

  const onReset = () => {
    setInitialState(false);

    ReactEditor.focus(editor);
  };

  usePressEnter({ isCompleted: status === StatusType.Completed, onNext, onCheck });

  return (
    <div className={styles.sentenceTask}>
      <div className={styles.content}>
        <div className={styles.children}>{children}</div>

        <div className={cx(styles.slate, { [styles.slate_alignCenter]: alignCenter })}>
          <div className={styles.slate_wrapper}>
            {initialValue && !noText && (
              <form spellCheck="false">
                <Slate editor={editor} initialValue={initialValue}>
                  <Editable
                    className={styles.textArea}
                    readOnly={status === "completed" || ["listen", "read", "select"].includes(activeType)}
                    onKeyDown={() => setStatus(StatusType.Editing)}
                    renderLeaf={renderLeaf}
                    //renderElement={(props) => <StoryElement isActive={activeSent === props.element.id} play={play} {...props} />}
                  />
                </Slate>
              </form>
            )}
            {status !== "completed" && (
              <Button
                size={"small"}
                type={"text"}
                className={styles.btn__clear}
                icon={<CloseOutlined style={{ fontSize: 14 }} />}
                onClick={onReset}
              />
            )}
          </div>
          <div className={styles.translate}>{text}</div>
        </div>

        <div className={styles.bottom}>
          <Button
            icon={<SoundFilled />}
            onClick={() => audio?.play()}
            style={{ visibility: status === "completed" ? "visible" : "hidden" }}
          />
        </div>
      </div>

      <TaskPanel
        isLoading={isLoading}
        lessonId={lesson.id}
        task={TaskType.Reverse}
        sentId={id}
        storyId={storyId}
        onCheck={onCheck}
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
        setDictOpened={setDictOpened}
        audio={audio}
        showGrammar={showGrammar}
        onHint={onHintAnswer}
        nextButton={button}
      ></TaskPanel>
    </div>
  );
};

export default SentenceTask;
